// session_timeout_dialog/session_timeout_dialog.component.ts
/*
 * This component, `SessionTimeoutDialog`, serves as the user-facing dialog
 * that displays a countdown timer when a user's session is about to expire due to inactivity.
 *
 * It is designed to be opened by the `SessionTimeoutService`.
 *
 * Key functionalities:
 *
 * 1.  **Synchronization with Server Time:** The dialog does not use its own hardcoded timer.
 * Instead, it receives the exact `remaining_time_s` from the `SessionTimeoutService`
 * as an `@Input()`. This ensures the countdown is always synchronized with the
 * server's session expiration timestamp, providing the user with accurate information.
 *
 * 2.  **Countdown Logic:**
 * - When the component initializes via `ngOnInit`, it uses the `remaining_time_s`
 * input to start a one-second `timer()` using RxJS.
 * - The `map` operator decrements `remaining_time_s` every second.
 * - The `take()` operator ensures the timer stops automatically when the countdown reaches zero.
 * - A `countdown_subscription` is managed to prevent memory leaks by unsubscribing in `ngOnDestroy`.
 *
 * 3.  **User Interaction:**
 * - The `dismiss()` method allows the user to close the dialog, which would typically
 * be used in conjunction with a "Keep Session" or "Refresh" button in the dialog's HTML.
 * - Closing the dialog signals the `SessionTimeoutService` to clear its internal timers.
 *
 * 4.  **Error Handling:** It includes a safeguard in `ngOnInit` to set a default `remaining_time_s`
 * of 60 seconds if the `@Input()` is not provided, preventing a timer from misbehaving.
 *
 */
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { NbDialogRef } from '@nebular/theme'
import { Subscription, timer } from 'rxjs'
import { map, take } from 'rxjs/operators'

@Component( {
  selector: 'ff-session-timeout-dialog',
  templateUrl: 'session_timeout_dialog.component.html',
  styleUrls: ['session_timeout_dialog.component.scss'],
} )
export class SessionTimeoutDialog implements OnInit, OnDestroy {

  @Input() title: string
  @Input() remaining_time_s: number // Use the same snake_case variable name

  private countdown_subscription: Subscription

  constructor( protected ref: NbDialogRef<SessionTimeoutDialog> ) {
    // The timer will now be initialized in ngOnInit to ensure the @Input() value is available.
  }

  ngOnInit () {
    // Check if remaining_time_s was provided, otherwise default to a safe value.
    if ( !this.remaining_time_s || this.remaining_time_s <= 0 ) {
      this.remaining_time_s = 60 // Default to 60 seconds if no value is passed.
    }

    // Use the passed-in remaining time to start the countdown.
    this.countdown_subscription = timer( 0, 1000 ).pipe(
      take( this.remaining_time_s + 1 ), // Take one more to show 0
      map( () => {
        if ( this.remaining_time_s >= 0 ) {
          --this.remaining_time_s
        } else {
          // You might want to automatically close the dialog here
          this.dismiss()
        }
      } ),
    ).subscribe()
  }

  ngOnDestroy () {
    // Clean up the subscription to prevent memory leaks
    if ( this.countdown_subscription ) {
      this.countdown_subscription.unsubscribe()
    }
  }

  dismiss () {
    this.ref.close()
  }
}
